import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Users,
  ArrowLeft,
  AlertCircle,
  MessageSquare,
  Flame,
  HelpCircle,
  Pin,
  Activity,
  ThumbsUp,
  Award,
  TrendingUp,
  Sparkles,
  ArrowUpRight,
  FileText,
} from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';
import { isAuthenticated, getCertifiedNetworkStatus, getAuthHeader } from '../../services/otpAuthService';
import SearchBar from './components/SearchBar';

const API_BASE_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace('/graphql', '')
  : 'http://localhost:5000';

const getIcon = (name: string) => {
  const Icon = (LucideIcons as any)[name];
  return Icon || MessageSquare;
};

interface AreaCard {
  _id: string;
  slug: string;
  displayName: string;
  description: string;
  icon: string;
  color: string;
  stats: {
    discussionsCount: number;
    participantsCount: number;
  };
}

interface DiscussionItem {
  _id: string;
  title: string;
  repliesCount: number;
  createdAt: string;
  lastActivityAt: string;
  status: string;
  area?: { slug: string; displayName: string; icon: string; color: string };
  author?: { businessName: string } | null;
}

interface HomepageData {
  areas: AreaCard[];
  highlights: {
    pinned: DiscussionItem[];
    hot: DiscussionItem[];
    unanswered: DiscussionItem[];
  };
  myStats: {
    points: number;
    level: string;
    stats: {
      discussions: number;
      replies: number;
      acceptedAnswers: number;
      reactionsReceived: number;
    };
  };
  globalStats: {
    totalMembers: number;
    discussionsToday: number;
  };
  recentActivity: DiscussionItem[];
}

const CommunityV2Page: React.FC = () => {
  const toast = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [accessMessage, setAccessMessage] = useState('');
  const [data, setData] = useState<HomepageData | null>(null);

  useEffect(() => {
    const checkAccessAndLoad = async () => {
      if (!isAuthenticated()) {
        toast.error("Devi effettuare l'accesso");
        navigate('/accesso-associati');
        return;
      }

      try {
        const status = await getCertifiedNetworkStatus();

        if (!status.isMember) {
          setAccessDenied(true);
          setAccessMessage('La Community è riservata ai membri della Rete Certificata UNASCA.');
          return;
        }

        if (status.currentCertificateStatus !== 'ATTIVO') {
          setAccessDenied(true);
          setAccessMessage(`Il tuo certificato non è attivo (stato: ${status.currentCertificateStatus}). Contatta UNASCA per maggiori informazioni.`);
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/community/v2/homepage`, {
          headers: getAuthHeader() as any
        });

        if (response.ok) {
          const result = await response.json();
          setData(result.data);
        } else {
          toast.error('Errore nel caricamento della community');
        }
      } catch (error) {
        console.error('Errore verifica accesso community v2:', error);
        toast.error("Errore durante la verifica dell'accesso");
        navigate('/area-riservata');
      } finally {
        setLoading(false);
      }
    };

    checkAccessAndLoad();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intenzionale: dipendenze ricreate a ogni render, aggiungerle cambierebbe la frequenza di esecuzione
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-750 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-indigo-200 dark:border-indigo-800 border-t-indigo-600 dark:border-t-indigo-400 rounded-full animate-spin" />
          <span className="text-xs text-gray-400 dark:text-gray-500 tracking-wide uppercase">Caricamento community...</span>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-8 text-center border border-gray-100 dark:border-gray-800">
          <div className="w-16 h-16 bg-red-50 dark:bg-red-950/50 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-500 dark:text-red-400" />
          </div>
          <h1 className="text-lg font-bold text-gray-900 dark:text-white mb-3">Accesso Negato</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{accessMessage}</p>
          <button
            onClick={() => navigate('/area-riservata')}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-100 text-white dark:text-gray-900 rounded-xl transition-colors text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna all'Area Riservata
          </button>
        </div>
      </div>
    );
  }

  const globalStats = data?.globalStats;
  const areas = data?.areas || [];
  const myStats = data?.myStats;
  const recentActivity = data?.recentActivity || [];

  const highlights: (DiscussionItem & { highlightType: string })[] = [];
  if (data?.highlights) {
    (data.highlights.pinned || []).forEach(d => highlights.push({ ...d, highlightType: 'pinned' }));
    (data.highlights.hot || []).forEach(d => highlights.push({ ...d, highlightType: 'hot' }));
    (data.highlights.unanswered || []).forEach(d => highlights.push({ ...d, highlightType: 'unanswered' }));
  }

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    const now = new Date();
    const diff = now.getTime() - d.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 1) return 'Pochi minuti fa';
    if (hours < 24) return `${hours}h fa`;
    const days = Math.floor(hours / 24);
    if (days === 1) return 'Ieri';
    if (days < 7) return `${days} giorni fa`;
    return d.toLocaleDateString('it-IT', { day: 'numeric', month: 'short' });
  };

  const getHighlightBadge = (type: string) => {
    switch (type) {
      case 'pinned':
        return { label: 'In Evidenza', color: 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 ring-1 ring-amber-200/60 dark:ring-amber-700/40', icon: Pin };
      case 'hot':
        return { label: 'Discussione Calda', color: 'bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300 ring-1 ring-orange-200/60 dark:ring-orange-700/40', icon: Flame };
      case 'unanswered':
        return { label: 'Cerca Risposta', color: 'bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300 ring-1 ring-rose-200/60 dark:ring-rose-700/40', icon: HelpCircle };
      default:
        return { label: '', color: 'bg-gray-100 text-gray-800', icon: MessageSquare };
    }
  };

  const getLevelInfo = (level?: string) => {
    const l = (level || '').toLowerCase();
    if (l === 'top_contributor') return { label: 'Top Contributor', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/30' };
    if (l === 'esperto') return { label: 'Esperto', color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-950/30' };
    if (l === 'attivo') return { label: 'Attivo', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-950/30' };
    return { label: 'Nuovo', color: 'text-gray-500 dark:text-gray-400', bg: 'bg-gray-50 dark:bg-gray-800' };
  };

  const levelInfo = getLevelInfo(myStats?.level);

  return (
    <div className="min-h-screen bg-[#f8f9fb] dark:bg-gray-950">
      {/* ═══ HEADER ═══ */}
      <div className="relative overflow-hidden">
        {/* Background layers */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-700 via-purple-950 to-slate-900" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(99,102,241,0.25),transparent)]" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-500/30 to-transparent" />

        <div className="relative max-w-6xl mx-auto px-4 pt-5 pb-8">
          {/* Top bar */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => navigate('/area-riservata')}
              className="p-2 -ml-2 hover:bg-white/5 rounded-xl transition-colors group"
            >
              <ArrowLeft className="w-5 h-5 text-indigo-300 group-hover:text-white transition-colors" />
            </button>
          </div>

          {/* Hero */}
          <div className="text-center mb-7">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 tracking-tight">
              Community Rete Certificata
            </h1>
            <p className="text-indigo-300/80 text-sm max-w-md mx-auto">
              Confrontati, chiedi e condividi con i colleghi della rete
            </p>
          </div>

          {/* Search */}
          <div className="max-w-lg mx-auto mb-6">
            <SearchBar className="[&_input]:bg-white/[0.08] [&_input]:border-white/[0.1] [&_input]:text-white [&_input]:placeholder-indigo-300/50 [&_input]:focus:ring-indigo-400/30 [&_input]:focus:bg-white/[0.12] [&_input]:backdrop-blur-sm [&_svg]:text-indigo-300/60" />
          </div>

          {/* Stats pills */}
          {globalStats && (
            <div className="flex flex-wrap justify-center gap-2.5">
              <div className="flex items-center gap-2 bg-white/[0.06] backdrop-blur-sm rounded-full px-4 py-1.5 text-[13px] text-indigo-200/80 border border-white/[0.06]">
                <Users className="w-3.5 h-3.5 text-indigo-400/70" />
                <span><strong className="text-white font-semibold">{globalStats.totalMembers}</strong> membri</span>
              </div>
              <div className="flex items-center gap-2 bg-white/[0.06] backdrop-blur-sm rounded-full px-4 py-1.5 text-[13px] text-indigo-200/80 border border-white/[0.06]">
                <TrendingUp className="w-3.5 h-3.5 text-emerald-400/70" />
                <span><strong className="text-white font-semibold">{globalStats.discussionsToday}</strong> nuove oggi</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ═══ MAIN CONTENT ═══ */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="flex flex-col lg:flex-row gap-6">

          {/* ── Left column ── */}
          <div className="flex-1 lg:w-[70%] space-y-6">

            {/* Le circolari hanno una sezione propria, fuori dalla community */}
            <Link
              to="/area-riservata/circolari"
              className="inline-flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
            >
              <FileText className="w-3.5 h-3.5" />
              <span>Le circolari UNASCA sono nella sezione <span className="font-medium underline">Comunicati e norme</span></span>
            </Link>

            {/* Da Non Perdere */}
            {highlights.length > 0 && (
              <section>
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-indigo-500" />
                  <h2 className="text-sm font-semibold text-gray-900 dark:text-white tracking-tight">Da Non Perdere</h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {highlights.map((item) => {
                    const badge = getHighlightBadge(item.highlightType);
                    const BadgeIcon = badge.icon;
                    return (
                      <Link
                        key={item._id + item.highlightType}
                        to={`/area-riservata/community-v2/discussione/${item._id}`}
                        className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4 hover:shadow-lg hover:border-gray-200 dark:hover:border-gray-700 transition-all duration-200 group"
                      >
                        <div className="flex items-center gap-2 mb-2.5">
                          <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${badge.color}`}>
                            <BadgeIcon className="w-3 h-3" />
                            {badge.label}
                          </span>
                        </div>
                        <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-2.5 line-clamp-2 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors leading-snug">
                          {item.title}
                        </h3>
                        <div className="flex items-center justify-between text-[11px] text-gray-400 dark:text-gray-500">
                          <span className="font-medium">{item.area?.displayName}</span>
                          <div className="flex items-center gap-3">
                            <span className="flex items-center gap-1">
                              <MessageSquare className="w-3 h-3" />
                              {item.repliesCount}
                            </span>
                            <span>{formatDate(item.createdAt)}</span>
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Aree Tematiche */}
            <section>
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare className="w-4 h-4 text-indigo-500" />
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white tracking-tight">Aree Tematiche</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {areas.map((area) => {
                  const AreaIcon = getIcon(area.icon);
                  return (
                    <Link
                      key={area._id}
                      to={`/area-riservata/community-v2/area/${area.slug}`}
                      className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4 hover:shadow-lg hover:border-gray-200 dark:hover:border-gray-700 transition-all duration-200 flex gap-3.5 group"
                    >
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-hover:scale-110"
                        style={{ backgroundColor: `${area.color}12`, border: `1px solid ${area.color}20` }}
                      >
                        <AreaIcon className="w-5 h-5" style={{ color: area.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <h3 className="text-sm font-medium text-gray-900 dark:text-white group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                            {area.displayName}
                          </h3>
                          <ArrowUpRight className="w-3.5 h-3.5 text-gray-300 dark:text-gray-600 group-hover:text-indigo-500 transition-all duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 flex-shrink-0" />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-500 line-clamp-2 mb-2 leading-relaxed">
                          {area.description}
                        </p>
                        <div className="flex items-center gap-3 text-[11px] text-gray-400 dark:text-gray-600">
                          <span className="flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            {area.stats?.discussionsCount || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" />
                            {area.stats?.participantsCount || 0}
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </section>
          </div>

          {/* ── Right column - Sidebar ── */}
          <div className="lg:w-[30%] space-y-4">

            {/* I Tuoi Contributi */}
            {myStats && (
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
                {/* Level header */}
                <div className={`px-5 py-3 ${levelInfo.bg} border-b border-gray-100 dark:border-gray-800`}>
                  <div className="flex items-center gap-2">
                    <Award className={`w-4 h-4 ${levelInfo.color}`} />
                    <span className={`text-xs font-semibold ${levelInfo.color} uppercase tracking-wider`}>{levelInfo.label}</span>
                    <span className="ml-auto text-[11px] text-gray-400 dark:text-gray-500 font-medium">{myStats.points} punti</span>
                  </div>
                </div>

                <div className="p-5 space-y-3.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                      <MessageSquare className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                      Discussioni
                    </span>
                    <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">{myStats.stats?.discussions || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                      <Activity className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                      Risposte
                    </span>
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">{myStats.stats?.replies || 0}</span>
                      {(myStats.stats?.acceptedAnswers || 0) > 0 && (
                        <span className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950/30 px-1.5 py-0.5 rounded-full">
                          {myStats.stats.acceptedAnswers} accettate
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
                      <ThumbsUp className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                      Reazioni
                    </span>
                    <span className="text-sm font-bold text-gray-900 dark:text-white tabular-nums">{myStats.stats?.reactionsReceived || 0}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Attività Recenti */}
            {recentActivity.length > 0 && (
              <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-5">
                <h3 className="text-xs font-semibold text-gray-900 dark:text-white mb-3.5 uppercase tracking-wider">Attività Recenti</h3>
                <div className="space-y-2.5">
                  {recentActivity.map((item) => (
                    <Link
                      key={item._id}
                      to={`/area-riservata/community-v2/discussione/${item._id}`}
                      className="flex gap-3 hover:bg-gray-50 dark:hover:bg-gray-800/50 rounded-lg p-2 -mx-2 transition-colors group"
                    >
                      <div className="w-1.5 h-1.5 rounded-full bg-indigo-400/60 mt-1.5 flex-shrink-0 group-hover:bg-indigo-500 transition-colors" />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs text-gray-700 dark:text-gray-300 line-clamp-2 leading-relaxed group-hover:text-gray-900 dark:group-hover:text-white transition-colors">{item.title}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {item.area?.displayName && (
                            <span className="text-[11px] font-medium text-indigo-500 dark:text-indigo-400">{item.area.displayName}</span>
                          )}
                          <span className="text-[11px] text-gray-400 dark:text-gray-600">{formatDate(item.lastActivityAt || item.createdAt)}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommunityV2Page;
